<template>
  <AdminLayout page-title="Blog Management" page-subtitle="Create, edit, and manage blog posts for your platform">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-2">Blog Management</h1>
        <p class="text-slate-400">Create, edit, and manage blog posts for your platform</p>
      </div>

      <!-- Action Bar -->
      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center space-x-4">
          <button 
            @click="showCreateModal = true"
            class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            New Post
          </button>
        </div>
        
        <div class="flex items-center space-x-4">
          <select v-model="statusFilter" class="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          
          <select v-model="categoryFilter" class="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg">
            <option value="">All Categories</option>
            <option value="Product">Product</option>
            <option value="Industry">Industry</option>
            <option value="Tips">Tips</option>
            <option value="Updates">Updates</option>
          </select>
        </div>
      </div>

      <!-- Blog Posts Table -->
      <div class="bg-slate-800 rounded-lg overflow-hidden">
        <div v-if="isLoading" class="p-8 text-center">
          <div class="text-slate-400">Loading blog posts...</div>
        </div>
        
        <div v-else-if="error" class="p-8 text-center">
          <div class="text-red-400 mb-4">{{ error }}</div>
          <button @click="loadBlogPosts" class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg">
            Try Again
          </button>
        </div>
        
        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-700">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Title</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Category</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Author</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Featured</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-slate-800 divide-y divide-slate-700">
              <tr v-for="post in filteredPosts" :key="post.id" class="hover:bg-slate-700/50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-white">{{ post.title }}</div>
                  <div class="text-sm text-slate-400 line-clamp-2">{{ post.excerpt }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getCategoryClass(post.category)" class="px-2 py-1 text-xs rounded-full">
                    {{ post.category }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {{ post.author }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusClass(post.status)" class="px-2 py-1 text-xs rounded-full">
                    {{ post.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="post.featured" class="text-emerald-400">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {{ formatDate(post.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="editPost(post)"
                      class="text-emerald-400 hover:text-emerald-300"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button 
                      @click="deletePost(post.id)"
                      class="text-red-400 hover:text-red-300"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div class="bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <h2 class="text-2xl font-bold text-white mb-6">
            {{ showEditModal ? 'Edit Blog Post' : 'Create New Blog Post' }}
          </h2>
          
          <form @submit.prevent="savePost" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Title</label>
                <input 
                  v-model="form.title"
                  type="text"
                  required
                  class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select 
                  v-model="form.category"
                  required
                  class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select Category</option>
                  <option value="Product">Product</option>
                  <option value="Industry">Industry</option>
                  <option value="Tips">Tips</option>
                  <option value="Updates">Updates</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Excerpt</label>
              <textarea 
                v-model="form.excerpt"
                required
                rows="3"
                class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
              ></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Content</label>
              <textarea 
                v-model="form.content"
                required
                rows="10"
                class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
              ></textarea>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Author</label>
                <input 
                  v-model="form.author"
                  type="text"
                  required
                  class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Status</label>
                <select 
                  v-model="form.status"
                  required
                  class="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg focus:border-emerald-500 focus:outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              
              <div class="flex items-center">
                <label class="flex items-center">
                  <input 
                    v-model="form.featured"
                    type="checkbox"
                    class="mr-2"
                  >
                  <span class="text-sm text-slate-300">Featured Post</span>
                </label>
              </div>
            </div>
            
            <div class="flex justify-end space-x-4">
              <button 
                type="button"
                @click="closeModal"
                class="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit"
                :disabled="isSaving"
                class="bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {{ isSaving ? 'Saving...' : (showEditModal ? 'Update Post' : 'Create Post') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { blogService, type BlogPost, type CreateBlogPostData } from '@/services/blogService'
import { computed, onMounted, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

// State
const blogPosts = ref<BlogPost[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)
const isSaving = ref(false)
const showCreateModal = ref(false)
const showEditModal = ref(false)
const editingPost = ref<BlogPost | null>(null)
const statusFilter = ref('')
const categoryFilter = ref('')

// Form data
const form = ref<CreateBlogPostData>({
  title: '',
  excerpt: '',
  content: '',
  category: '',
  author: '',
  featured: false,
  status: 'draft'
})

// Computed properties
const filteredPosts = computed(() => {
  let posts = blogPosts.value
  
  if (statusFilter.value) {
    posts = posts.filter(post => post.status === statusFilter.value)
  }
  
  if (categoryFilter.value) {
    posts = posts.filter(post => post.category === categoryFilter.value)
  }
  
  return posts
})

// Methods
const loadBlogPosts = async () => {
  try {
    isLoading.value = true
    error.value = null
    const posts = await blogService.getBlogPosts()
    blogPosts.value = posts
  } catch (err) {
    console.error('Error loading blog posts:', err)
    error.value = 'Failed to load blog posts'
  } finally {
    isLoading.value = false
  }
}

const getCategoryClass = (category: string) => {
  switch (category.toLowerCase()) {
    case 'product':
      return 'bg-emerald-500/20 text-emerald-400'
    case 'industry':
      return 'bg-cyan-500/20 text-cyan-400'
    case 'tips':
      return 'bg-amber-500/20 text-amber-400'
    case 'updates':
      return 'bg-purple-500/20 text-purple-400'
    default:
      return 'bg-slate-500/20 text-slate-400'
  }
}

const getStatusClass = (status: string) => {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/20 text-emerald-400'
    case 'draft':
      return 'bg-amber-500/20 text-amber-400'
    case 'archived':
      return 'bg-slate-500/20 text-slate-400'
    default:
      return 'bg-slate-500/20 text-slate-400'
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

const resetForm = () => {
  form.value = {
    title: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    featured: false,
    status: 'draft'
  }
}

const editPost = (post: BlogPost) => {
  editingPost.value = post
  form.value = {
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    category: post.category,
    author: post.author,
    featured: post.featured,
    status: post.status
  }
  showEditModal.value = true
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  editingPost.value = null
  resetForm()
}

const savePost = async () => {
  try {
    isSaving.value = true
    
    const token = localStorage.getItem('auth_token') || ''
    
    if (showEditModal.value && editingPost.value) {
      await blogService.updateBlogPost({
        id: editingPost.value.id,
        ...form.value
      }, token)
    } else {
      await blogService.createBlogPost(form.value, token)
    }
    
    await loadBlogPosts()
    closeModal()
  } catch (err) {
    console.error('Error saving blog post:', err)
    error.value = 'Failed to save blog post'
  } finally {
    isSaving.value = false
  }
}

const deletePost = async (id: number) => {
  if (!confirm('Are you sure you want to delete this blog post?')) {
    return
  }
  
  try {
    const token = localStorage.getItem('auth_token') || ''
    await blogService.deleteBlogPost(id, token)
    await loadBlogPosts()
  } catch (err) {
    console.error('Error deleting blog post:', err)
    error.value = 'Failed to delete blog post'
  }
}

// Lifecycle
onMounted(() => {
  loadBlogPosts()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
